import React, { useEffect, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { compact, forEach, get, includes, isArray, isEmpty, uniq, uniqBy } from "lodash";
import { Sidebar } from "primereact/sidebar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import VinWithType from "../../../../../components/Common/Display/VinWithType";
import Location from "../../../../../components/Common/Display/Location";

interface IFormProps {
    id: string | null;
    t: any | null;
    onRefresh?: any | null;
    onClose?: any | null;
    toastify?: any | null;
}

const ADD_LOAD_STATUS_ORDER = ['announced', 'accepted', 'confirmed', 'updated', 'in-transit'];

const normalizeLoadStatus = (status: any): string => `${ status ?? '' }`.toLowerCase();

const isBlankId = (value: any): boolean => value === null || value === undefined || String(value) === '';

const getAllowedAddLoadStatuses = (groupStatus: any): string[] => {
    const normalized = normalizeLoadStatus(groupStatus);

    if ( normalized === 'in-transit' ) {
        return [];
    }

    const index = ADD_LOAD_STATUS_ORDER.indexOf(normalized);
    if ( index < 0 ) {
        return ['announced'];
    }

    return ADD_LOAD_STATUS_ORDER.slice(0, index + 1);
};

const isDriverAndVehicleCompatible = (row: any, groupDriverId: any, groupTransportVehicleId: any): boolean => {
    const loadDriverId = get(row, 'alp_outbound_load_driver_id');
    const loadTransportVehicleId = get(row, 'alp_outbound_load_transport_vehicle_id');
    const loadIsBlank = isBlankId(loadDriverId) && isBlankId(loadTransportVehicleId);

    if ( loadIsBlank ) {
        return true;
    }

    return !isBlankId(groupDriverId)
        && !isBlankId(groupTransportVehicleId)
        && String(loadDriverId) === String(groupDriverId)
        && String(loadTransportVehicleId) === String(groupTransportVehicleId);
};

function AlpOutboundLoadGroupAddLoad(props: IFormProps) {
    const { t } = useTranslation();
    const { toastify } = props;
    const [panelState, setPanelState] = useState<boolean>(false);
    const [availableLoads, setAvailableLoads] = useState<any[]>([]);
    const [selectedAddLoads, setSelectedAddLoads] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const onClose = () => {
        setPanelState(false);
        setAvailableLoads([]);
        setSelectedAddLoads([]);
        if ( props.onClose ) {
            props.onClose();
        }
    };

    const getGroupProviderIds = (loads: any[]): string[] => {
        const ids: string[] = [];
        forEach(loads, (load: any) => {
            const providerId = get(load, 'provider_id') || get(load, 'provider.id') || get(load, 'alp_outbound_load_orders.0.order.provider_id');
            if ( !isEmpty(providerId) ) {
                ids.push(String(providerId));
            }
        });

        return uniq(compact(ids));
    };

    const getExistingLoadGuids = (loads: any[]): string[] => {
        const ids: string[] = [];
        forEach(loads, (load: any) => {
            const id = String(get(load, 'guid') || get(load, 'id') || '');
            if ( !isEmpty(id) ) {
                ids.push(id);
            }
        });

        return uniq(compact(ids));
    };

    const fetchAvailableLoads = async (
        groupStatus: any,
        groupProviderIds: string[],
        existingLoadGuids: string[],
        groupDriverId: any,
        groupTransportVehicleId: any
    ) => {
        const allowedAddLoadStatuses = getAllowedAddLoadStatuses(groupStatus);
        const response = await request({
            url: `/suppliers/orders/transport/requests?1=1`,
            params: {
                per_page: 0,
                filters: {
                    providers: groupProviderIds,
                    alp_outbound_load_statuses: allowedAddLoadStatuses,
                    alp_outbound_group_driver_id: groupDriverId,
                    alp_outbound_group_transport_vehicle_id: groupTransportVehicleId,
                },
            }
        });

        const rows = response?.data?.data ?? response?.data ?? [];
        const list = isArray(rows) ? rows : [];
        const filtered = list.filter((row: any) => (
            String(get(row, 'source') || '') === 'alp_outbound_load_order'
            && includes(allowedAddLoadStatuses, normalizeLoadStatus(get(row, 'load_status') || get(row, 'status')))
            && !includes(existingLoadGuids, String(get(row, 'alp_outbound_load_guid') || ''))
            && (isEmpty(groupProviderIds) || includes(groupProviderIds, String(get(row, 'provider_id'))))
            && isDriverAndVehicleCompatible(row, groupDriverId, groupTransportVehicleId)
        ));

        setAvailableLoads(uniqBy(filtered, (row: any) => String(get(row, 'alp_outbound_load_guid') || get(row, 'id') || '')));
    };

    const getDetail = async (groupToken: string) => {
        setLoading(true);
        try {
            const detailResponse = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });

            const detailData = detailResponse?.data?.data ?? detailResponse?.data ?? {};
            const loads = Array.isArray(detailData?.alp_outbound_loads) ? detailData.alp_outbound_loads : [];

            if ( loads.length <= 0 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
                onClose();
                return;
            }

            setPanelState(true);
            setSelectedAddLoads([]);
            const groupDriverId = get(detailData, 'driver_id') || get(loads, '0.driver_id');
            const groupTransportVehicleId = get(detailData, 'transport_vehicle_id') || get(loads, '0.transport_vehicle_id');
            await fetchAvailableLoads(
                detailData?.status,
                getGroupProviderIds(loads),
                getExistingLoadGuids(loads),
                groupDriverId,
                groupTransportVehicleId
            );
        } catch (e: any) {
            if ( e.status === 404 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const applyAddLoads = async () => {
        const rows = isArray(selectedAddLoads) ? selectedAddLoads : [];
        const loadIds = compact(
            uniq(rows.map((row: any) => String(get(row, 'alp_outbound_load_guid') || '')))
        );

        if ( loadIds.length <= 0 ) {
            toastify && toastify(t("no record selected"), "info");
            return;
        }

        if ( isEmpty(props.id) ) {
            toastify && toastify(t("action not allowed: 404"), "error");
            return;
        }

        setLoading(true);
        try {
            await request({
                method: 'POST',
                url: `/suppliers/provider/aviso/outbound/loads/${ props.id }/update`,
                data: {
                    new_loads: loadIds,
                },
            });

            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            onClose();
            if ( props.onRefresh ) {
                props.onRefresh();
            }
        } catch (e: any) {
            if ( e.status === 422 ) {
                toastify && toastify(t("unprocessable entity"), "error");
            } else if ( e.status === 404 ) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if ( !isEmpty(props.id) ) {
            getDetail(String(props.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id]);

    return (
        <Sidebar
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className={ "card-title mt-0 mb-1" }>{ t('add') } { t('load') }</h5>
                    <div className="text-muted small">
                        { t("group token") }: <strong>{ props.id || "-" }</strong>
                    </div>
                </div>
            }
            visible={ panelState }
            position="right"
            onHide={ onClose }
            blockScroll={ true }
            closeOnEscape={ false }
            style={ { width: '1100px' } }
        >
            <div className="p-3 pt-0">
                { loading && <div className="text-muted mb-2">{ t('loading') }</div> }

                <div className="alert alert-info py-2 small mb-3">
                    <strong>{ t('Add Load Rules') }</strong>
                    <ol className="mb-0 ps-3 mt-1">
                        <li>{ t('Allowed status order: announced, accepted, confirmed, updated.') }</li>
                        <li>{ t('Same or lower-status loads can be added; lower-status loads are promoted after adding.') }</li>
                        <li>{ t('Only same-provider, ungrouped loads are shown.') }</li>
                        <li>{ t('Driver and transport vehicle must match the group, or both must be blank.') }</li>
                    </ol>
                </div>

                <DataTable
                    value={ availableLoads }
                    dataKey="alp_outbound_load_guid"
                    selection={ selectedAddLoads }
                    onSelectionChange={ (e: any) => setSelectedAddLoads(e.value || []) }
                    scrollable
                    scrollHeight="620px"
                    stripedRows
                    showGridlines
                >
                    <Column selectionMode="multiple" headerStyle={ { width: '3rem' } } />
                    <Column field="load_number" header={ t('load number') } />
                    <Column field="vin_number" header={ t('vin number') }
                        body={(row: any) => <VinWithType vinNumber={ row.vin ?? row.vin_number } />}
                    />
                    <Column field="order_cd" header={ t('order') } />
                    <Column field="ldg_nr" header={ t('ldg nr') } />
                    <Column field="load_status" header={ t('status') } />
                    <Column field="emil_frey_supplier_compound" header={ t('supplier compound') } />
                    <Column field="emil_frey_dealer" header={ t('dealer') } />
                    <Column
                        header={ t('from location') }
                        body={ (row: any) => row?.current_journey?.from_location ? <Location item={ row.current_journey.from_location } titled={ 1 } /> : "-" }
                    />
                    <Column
                        header={ t('to location') }
                        body={ (row: any) => row?.current_journey?.to_location ? <Location item={ row.current_journey.to_location } titled={ 1 } /> : "-" }
                    />
                </DataTable>
            </div>

            <div className="filter-card-footer" style={ { width: '1085px' } }>
                <Button
                    type="button"
                    size={ "small" }
                    outlined
                    severity="secondary"
                    onClick={ onClose }
                    disabled={ loading }
                >
                    { t('cancel') }
                </Button>
                <Button
                    type="button"
                    className={ "ms-2" }
                    size={ "small" }
                    severity="success"
                    onClick={ applyAddLoads }
                    disabled={ (selectedAddLoads || []).length <= 0 || loading }
                >
                    <i className="pi pi-plus me-2" />
                    { t('add') }
                </Button>
            </div>
        </Sidebar>
    );
}

AlpOutboundLoadGroupAddLoad.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpOutboundLoadGroupAddLoad);
